import React, { useState, useEffect } from 'react';
import { 
  Users, TrendingUp, Activity, BarChart2, Search, Download, Award
} from 'react-feather'; // O lucide-react, lo que estés usando
import { API_URL, fetchWithAuth, getImageUrl } from '../../api';

// Subcomponente de Tarjeta
const MetricCard = ({ title, value, icon, colorClass }) => (
  <div className="bg-black/40 backdrop-blur-md rounded-2xl p-5 border border-white/10 flex flex-col items-center justify-center text-center shadow-lg hover:bg-white/5 transition-colors">
    <div className={`mb-3 p-3 rounded-full bg-white/5 ${colorClass}`}>
      {icon}
    </div>
    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{title}</h3>
    <p className="text-2xl font-bold text-white">{value}</p>
  </div>
);

const EventMetricsTab = ({ eventId }) => {
  // --- ESTADOS ---
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [acceptedUsers, setAcceptedUsers] = useState([]);
  const [qrRequests, setQrRequests] = useState([]); // Si lo necesitas para algo más específico
  const [searchQuery, setSearchQuery] = useState('');
  
  const [metrics, setMetrics] = useState({
    attendedCount: 0,
    averageRequestsPerDay: 0,
    totalRequests: 0,
    averageQrsPerPromoter: 0,
    topPromoters: []
  });

  // --- FETCH DATA ---
  useEffect(() => {
    const fetchAllMetrics = async () => {
      try {
        setLoading(true);
        
        // 1. Cargar lista de asistentes aceptados
        const usersResponse = await fetchWithAuth(`${API_URL}/events/${eventId}/accepted-users`);
        setAcceptedUsers(Array.isArray(usersResponse) ? usersResponse : []);

        // 2. Cargar todas las métricas en paralelo
        const [
          attendedRes,
          requestsPerDayRes,
          totalRequestsRes,
          promoterPerformanceRes,
          topPromotersRes
        ] = await Promise.all([
          fetchWithAuth(`${API_URL}/events/${eventId}/metrics/attended`),
          fetchWithAuth(`${API_URL}/events/${eventId}/metrics/requests-per-day`),
          fetchWithAuth(`${API_URL}/events/${eventId}/metrics/total-requests`),
          fetchWithAuth(`${API_URL}/events/${eventId}/metrics/promoter-performance`),
          fetchWithAuth(`${API_URL}/events/${eventId}/metrics/top-promoters`)
        ]);

        setMetrics({
          attendedCount: attendedRes.attended_count || 0,
          averageRequestsPerDay: requestsPerDayRes.average_requests_per_day || 0,
          totalRequests: totalRequestsRes.total_requests || 0,
          averageQrsPerPromoter: promoterPerformanceRes.average_qrs_per_promoter || 0,
          topPromoters: topPromotersRes.top_promoters || []
        });

      } catch (err) {
        console.error('Error fetching metrics:', err);
        setError('No se pudieron cargar algunas métricas.');
      } finally {
        setLoading(false);
      }
    };

    if (eventId) {
      fetchAllMetrics();
    }
  }, [eventId]);

  // --- EXPORTAR ---
  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/events/${eventId}/referral-report`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte_asistentes_${eventId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export error:', error);
      alert('Error al exportar el reporte.');
    }
  };

  // Filtrar usuarios
  const filteredUsers = acceptedUsers.filter(user => 
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) return <div className="py-20 text-center text-white/50 animate-pulse">Cargando métricas...</div>;

  return (
    <div className="w-full max-w-5xl mx-auto pb-20 pt-16 fade-in px-4 md:px-0">
      
      {/* HEADER & EXPORT */}
      <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-4">
        <h2 className="text-2xl font-light tracking-wider text-white">
          MÉTRICAS DEL EVENTO
        </h2>
        <button
            onClick={handleExport}
            className="px-5 py-2.5 bg-green-600/90 hover:bg-green-600 text-white rounded-xl font-medium transition-colors flex items-center gap-2 shadow-lg shadow-green-900/20 text-sm"
          >
            <Download size={18} />
            Exportar CSV
        </button>
      </div>

      {/* 1. GRID DE KPIs (Responsive) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <MetricCard 
          title="Total Asistentes" 
          value={metrics.attendedCount} 
          icon={<Users size={20} />} 
          colorClass="text-blue-400"
        />
        <MetricCard 
          title="Solicitudes / Día" 
          value={metrics.averageRequestsPerDay} 
          icon={<TrendingUp size={20} />} 
          colorClass="text-green-400"
        />
        <MetricCard 
          title="Total Solicitudes" 
          value={metrics.totalRequests} 
          icon={<Activity size={20} />} 
          colorClass="text-purple-400"
        />
        <MetricCard 
          title="QRs / Promotor" 
          value={metrics.averageQrsPerPromoter} 
          icon={<Award size={20} />} 
          colorClass="text-yellow-400"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* 2. TOP PROMOTORES (Columna Izquierda o Arriba) */}
        <div className="lg:col-span-1 space-y-6">
           <div className="bg-black/40 backdrop-blur-md rounded-2xl border border-white/10 overflow-hidden min-h-[400px]">
              <div className="p-5 border-b border-white/10 flex items-center gap-2 bg-white/5">
                <Award size={20} className="text-yellow-400" />
                <h3 className="font-medium text-white">Top Promotores</h3>
              </div>
              
              <div className="divide-y divide-white/5">
                {metrics.topPromoters.length === 0 ? (
                  <div className="p-8 text-center text-gray-500 text-sm">
                    No hay datos de promotores aún.
                  </div>
                ) : (
                  metrics.topPromoters.map((promoter, index) => (
                    <div key={index} className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors group">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold 
                          ${index === 0 ? 'bg-yellow-500 text-black' : 
                            index === 1 ? 'bg-gray-400 text-black' : 
                            index === 2 ? 'bg-orange-700 text-white' : 'bg-white/10 text-white'}`}>
                          {index + 1}
                        </div>
                        <div>
                           <p className="text-sm font-medium text-white">{promoter.promoter_name}</p>
                           <p className="text-xs text-gray-500">Promotor</p>
                        </div>
                      </div>
                      <span className="text-sm font-bold text-white bg-white/10 px-2 py-1 rounded-md group-hover:bg-white/20">
                        {promoter.referral_count}
                      </span>
                    </div>
                  ))
                )}
              </div>
           </div>
        </div>

        {/* 3. LISTA DE ASISTENTES (Columna Derecha - Más ancha) */}
        <div className="lg:col-span-2">
          <div className="bg-black/40 backdrop-blur-md rounded-2xl border border-white/10 overflow-hidden min-h-[400px]">
            {/* Header Lista */}
            <div className="p-5 border-b border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white/5">
               <div className="flex items-center gap-2">
                  <BarChart2 size={20} className="text-purple-400" />
                  <h3 className="font-medium text-white">
                    Asistentes Aceptados <span className="text-white/40 text-sm ml-2">({acceptedUsers.length})</span>
                  </h3>
               </div>
               
               {/* Buscador */}
               <div className="relative w-full sm:w-64">
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 bg-black/20 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-white/30"
                  />
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
               </div>
            </div>

            {/* Lista Scrollable */}
            <div className="max-h-[500px] overflow-y-auto custom-scrollbar">
              {filteredUsers.length === 0 ? (
                 <div className="p-10 text-center text-gray-500">
                    {searchQuery ? 'No se encontraron resultados.' : 'Aún no hay asistentes aceptados.'}
                 </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {filteredUsers.map((user) => (
                    <div key={user.id} className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors">
                      <div className="flex items-center gap-3">
                         <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-900 to-blue-900 border border-white/10 flex items-center justify-center overflow-hidden">
                            {user.profile_image_url ? (
                                <img src={getImageUrl(user.profile_image_url)} className="w-full h-full object-cover" alt={user.name}/>
                            ) : (
                                <span className="text-xs font-bold text-white">{user.name?.charAt(0).toUpperCase()}</span>
                            )}
                         </div>
                         <div>
                            <p className="text-sm font-medium text-white">{user.name}</p>
                            <p className="text-xs text-gray-500">@{user.username}</p>
                         </div>
                      </div>
                      
                      <div className="text-right">
                         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20">
                            Aceptado
                         </span>
                         {user.events_assisted > 0 && (
                            <p className="text-[10px] text-gray-500 mt-1">{user.events_assisted} eventos prev.</p>
                         )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

      </div>

      {error && (
        <div className="mt-8 text-center p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300">
          {error}
        </div>
      )}
    </div>
  );
};

export default EventMetricsTab;